import React, { useState, useEffect, useCallback } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { PageHeader } from '../../../components/Layout'
import { constants } from '../../../utils'
import { appService, renewEnrollmentService, renewEnrollmentValidatorService } from '../../../services'
import {
  Error,
  SubscriptionConflictDialog,
  StudentCpfNotFoundDialog
} from '../../../components'

import { Renewing, Done, Form, Found, Selection } from './elements'

const Search = () => {
  const history = useHistory()
  const location = useLocation()

  const [fetched, setFetched] = useState(false)
  const [publicCall, setPublicCall] = useState(null)
  const [notFound, setNotFound] = useState(false)
  const [emptyGroupStudent, setEmptyGroupStudent] = useState(false)
  const [loading, setLoading] = useState(false)
  const [enrollments, setEnrollments] = useState([])
  const [enrollment, setEnrollment] = useState(null)
  const [renewing, setRenewing] = useState(null)
  const [done, setDone] = useState(false)
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false)
  const [subscriptionProtocol, setSubscriptionProtocol] = useState(null)
  const [pendingEnrollments, setPendingEnrollments] = useState(null)
  const [redirectError, setRedirectError] = useState(null)
  const [studentCpfDialogOpen, setStudentCpfDialogOpen] = useState(false)

  const fetchSubscriptionConflict = async enrollmentsList => {
    if (!Array.isArray(enrollmentsList) || enrollmentsList.length === 0) return null

    try {
      const results = await Promise.allSettled(
        enrollmentsList.map(item => renewEnrollmentValidatorService.checkHasSubscription(item.id))
      )

      const rejected = results.find(r => r.status === 'rejected')

      return rejected ? rejected.reason || {} : null
    } catch {
      return null
    }
  }

  useEffect(() => {
    if (appService.shouldRedirectToHome('renew')) {
      history.push(constants.routes.home.path)
    }
  }, [])

  const refreshEnrollmentIfRenewEnrollment = useCallback(async () => {
    try {
      const enrollmentId = enrollment && enrollment.id
      const studentCpf = enrollment && enrollment.studentCpf
      if (!enrollmentId && !studentCpf) return

      if (studentCpf) {
        const { success, payload } = await renewEnrollmentService.search(studentCpf)

        if (success && payload) {
          const next = Array.isArray(payload)
            ? (payload.find(i => i && i.id === enrollmentId) || payload[0])
            : payload
          if (next) updateEnrollment(next)
          return
        }
      }
    } catch { }
  }, [enrollment && enrollment.id, enrollment && enrollment.studentCpf])

  useEffect(() => {
    if (location && location.state && location.state.error) {
      setRedirectError(location.state.error)
      setRenewing(false)
      setDone(false)
      refreshEnrollmentIfRenewEnrollment()
      history.replace({ ...location, state: {} })
    }
  }, [location, refreshEnrollmentIfRenewEnrollment])

  useEffect(() => {
    if (enrollments.length === 1 && !enrollment) {
      setEnrollment(enrollments[0])
    }
  }, [enrollments])

  useEffect(() => {
    tryToFindInfo()
  }, [])

  const tryToFindInfo = async () => {
    const {
      success,
      payload: { info },
    } = await appService.getPublicCallInfo()

    if (success) {
      setPublicCall(info)
    }

    setLoading(!success)
  }

  const updateEnrollment = payload => {
    setEnrollments(prev => prev.map(i => (i.id === payload.id ? payload : i)))
    setEnrollment(payload)
  }

  const backToEnrollment = () => {
    setDone(false)
    setRenewing(false)
  }

  const tryToLogin = (user, password) => async () => {
    setLoading(true)

    const { success, payload } = await renewEnrollmentService.get(
      user,
      password
    )

    if (success) {
      if (payload.length === 0) {
        setLoading(false)
        setEmptyGroupStudent(true)
        return
      }

      if (!payload[0].studentCpf) {
        setStudentCpfDialogOpen(true)
        setLoading(false)
        return
      }

      const conflict = await fetchSubscriptionConflict(payload)

      if (!conflict) {
        setEnrollments(payload)
        setFetched(true)
        setNotFound(false)
      } else {
        setPendingEnrollments(payload)
        setSubscriptionProtocol(conflict.subscriptionProtocol || null)
        setSubscriptionDialogOpen(true)
      }
    } else {
      setNotFound(true)
    }

    setLoading(false)
  }

  const handleSubscriptionModalCancel = event => {
    if (event && event.currentTarget && typeof event.currentTarget.blur === 'function') {
      event.currentTarget.blur()
    }

    setSubscriptionDialogOpen(false)
    setPendingEnrollments(null)
  }

  const handleSubscriptionModalConfirm = event => {
    if (event && event.currentTarget && typeof event.currentTarget.blur === 'function') {
      event.currentTarget.blur()
    }

    setSubscriptionDialogOpen(false)
    if (pendingEnrollments) {
      setEnrollments(pendingEnrollments)
      setFetched(true)
      setNotFound(false)
      setPendingEnrollments(null)
    }
  }

  const renderFormOrEnrollmentSelection = () =>
    fetched ? (
      <Selection data={enrollments} setEnrollment={setEnrollment} />
    ) : (
      <Form loading={loading} notFound={notFound} emptyGroupStudent={emptyGroupStudent} tryToLogin={tryToLogin} publicCall={publicCall} />
    )

  const renderFoundOrRenewing = () => {
    if (done) {
      return <Done data={enrollment} backToEnrollment={backToEnrollment} />
    } else if (renewing) {
      return (
        <Renewing
          data={enrollment}
          setDone={setDone}
          setEnrollment={updateEnrollment}
          onBack={backToEnrollment}
          publicCall={publicCall}
        />
      )
    }

    return (
      <Found
        data={enrollment}
        done={done}
        setEnrollment={updateEnrollment}
        setRenewing={setRenewing}
      />
    )
  }

  return (
    <>
      <PageHeader
        buttonText="Voltar"
        title="Solicitar renovação de matrícula (Rematrícula)"
        onClick={
          done
            ? false
            : () => {
              setRedirectError(null)
              if (renewing) {
                setRenewing(false)
              } else if (enrollment) {
                setEnrollment(null)

                if (enrollments.length < 2) {
                  setEnrollments([])
                  setFetched(false)
                }
              } else {
                history.push(constants.routes.home.path)
              }
            }
        }
      />

      {!!redirectError && (
        <div style={{ margin: '0 24px 16px' }}>
          <Error>
            <span>{redirectError}</span>
          </Error>
        </div>
      )}

      {enrollment ? renderFoundOrRenewing() : renderFormOrEnrollmentSelection()}

      <SubscriptionConflictDialog
        open={subscriptionDialogOpen}
        protocol={subscriptionProtocol}
        onCancel={handleSubscriptionModalCancel}
        onConfirm={handleSubscriptionModalConfirm}
      />

      <StudentCpfNotFoundDialog
        open={studentCpfDialogOpen}
        onClose={() => {
          setStudentCpfDialogOpen(false)
          history.push(constants.routes.home.path)
        }}
      />
    </>
  )
}

export default Search
